import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard, CalendarDays, ClipboardList, Scissors, Users, UserCog,
  Package, ReceiptText, LogOut, Sparkles, Menu, X, Zap, ShoppingBag, ShoppingCart, 
  Truck, IndianRupee, FileText, PlaneTakeoff, BarChart3, Settings, LifeBuoy, Home,
} from "lucide-react";
import { useState, useEffect, useRef } from "react";
import { norm } from "@/lib/normalize";
import { toast } from "sonner";
import { useIdleTimer } from "react-idle-timer";

const SECTIONS = [
  {
    heading: "MAIN MENU",
    items: [
      { to: "/dashboard", icon: Home, label: "Home", roles: ["admin"], end: true },
      { to: "/dashboard/book", icon: ClipboardList, label: "Book Appointment", roles: ["admin", "staff", "customer", "receptionist"] },
      { to: "/dashboard/appointments", icon: CalendarDays, label: "Appointments", roles: ["admin", "staff", "customer", "receptionist"] },
      { to: "/dashboard/calendar", icon: CalendarDays, label: "Calendar", roles: ["admin", "staff", "customer"] },
      // { to: "/dashboard/analytics", icon: BarChart3, label: "Analytics", roles: ["admin", "staff"] },
    ],
  },
  {
    heading: "PRODUCT",
    items: [
      { to: "/dashboard/services", icon: Scissors, label: "Services", roles: ["admin", "staff", "customer", "receptionist"] },
      { to: "/dashboard/inventory", icon: Package, label: "Inventory", roles: ["admin", "staff"] },
      { to: "/dashboard/invoices", icon: ReceiptText, label: "Invoices", roles: ["admin", "staff", "receptionist"] },
      { to: "/dashboard/product-sale", icon: ShoppingBag, label: "Product Sale", roles: ["admin", "staff", "receptionist"] },
      { to: "/dashboard/products", icon: ShoppingBag, label: "Products", roles: ["admin", "receptionist"] },
      { to: "/dashboard/suppliers", icon: Truck, label: "Suppliers", roles: ["admin", "receptionist"] },
      { to: "/dashboard/purchases", icon: ShoppingCart, label: "Purchases", roles: ["admin", "receptionist"] },
    ],
  },
  {
    heading: "TEAM",
    items: [
      { to: "/dashboard/staff", icon: UserCog, label: "Employees", roles: ["admin", "staff", "receptionist"] },
      { to: "/dashboard/users", icon: Users, label: "User Accounts", roles: ["admin"] },
      { to: "/dashboard/customers", icon: Users, label: "Customers", roles: ["admin", "staff", "receptionist"] },
      { to: "/dashboard/payroll", icon: IndianRupee, label: "Payroll", roles: ["admin"] },
      { to: "/dashboard/leave", icon: PlaneTakeoff, label: "Leave Request", roles: ["admin", "staff"] },
      { to: "/dashboard/salary-rules", icon: FileText, label: "Salary Rules", roles: ["admin"] },
    ],
  },
  {
    heading: "OTHER",
    items: [
      { to: "/dashboard/expenses", icon: ReceiptText, label: "Expenses", roles: ["admin"] },
      { to: "/dashboard/settings", icon: Settings, label: "Settings", roles: ["admin"] },
      { to: "/dashboard/help", icon: LifeBuoy, label: "Help & Support", roles: ["admin", "staff", "customer"] },
    ],
  },
];

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const mainRef = useRef(null);

  // Scroll content area to top on route change
  useEffect(() => {
    if (mainRef.current) {
      mainRef.current.scrollTo(0, 0);
    }
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const role = norm(user.roleName);
  const filteredSections = SECTIONS.map((s) => ({
    ...s,
    items: s.items.filter((i) => i.roles.includes(role)),
  })).filter((s) => s.items.length > 0);

    useIdleTimer({
        timeout: 30 * 60 * 1000,
        onIdle: logout
    });

  return (
    <div className="h-screen bg-emerald-base text-emerald-text flex overflow-hidden">
      {/* Sidebar */}
      <aside className={`fixed lg:static inset-y-0 left-0 z-40 w-72 bg-emerald-surface border-r border-gold-subtle transform transition-transform lg:transform-none ${open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}>
        <div className="h-full flex flex-col">
          <div className="px-6 py-6 border-b border-gold-subtle flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-gold" />
              <div>
                <div className="font-heading text-2xl text-white leading-none">SOS</div>
                <div className="overline mt-1">Salon & Spa</div>
              </div>
            </div>
            <button className="lg:hidden text-emerald-muted" onClick={() => setOpen(false)} data-testid="sidebar-close-btn"><X className="w-5 h-5" /></button>
          </div>

          {/* AI Assistant pill (visual only) */}
          <div className="px-4 pt-4">
            <button
              type="button"
              data-testid="ai-assistant-btn"
              onClick={() => toast("AI Assistant is coming soon.", { description: "Sit tight — we're training something special." })}
              className="w-full flex items-center gap-2 px-4 py-2.5 rounded-full text-sm ai-pill-dark transition"
            >
              <Sparkles className="w-4 h-4" /> AI Assistant
            </button>
          </div>

          <nav className="flex-1 overflow-y-auto py-4 px-4 space-y-5">
            {filteredSections.map((section) => (
              <div key={section.heading}>
                <div className="px-2 pb-2 text-[10px] tracking-[0.24em] text-emerald-muted font-semibold">{section.heading}</div>
                <div className="space-y-1">
                  {section.items.map(({ to, icon: Icon, label, end }) => (
                    <NavLink
                      key={to} to={to} end={end}
                      data-testid={`nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
                      onClick={() => setOpen(false)}
                      className={({ isActive }) =>
                        `flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm transition ${
                          isActive
                            ? "bg-[rgba(197,160,89,0.14)] text-gold border border-gold-subtle"
                            : "text-emerald-muted hover:bg-[rgba(197,160,89,0.06)] hover:text-white"
                        }`
                      }
                    >
                      <Icon className="w-4 h-4" /> {label}
                    </NavLink>
                  ))}
                </div>
              </div>
            ))}
          </nav>

          <div className="p-4 border-t border-gold-subtle">
            <div className="px-3 py-2 mb-2">
              <div className="text-sm text-white font-medium truncate" data-testid="user-name">{user.name}</div>
              <div className="text-xs text-emerald-muted capitalize">{norm(user.roleName)}</div>
            </div>
            <button onClick={() => { logout(); nav("/login"); }} data-testid="logout-btn"
              className="w-full flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm text-emerald-muted hover:text-gold hover:bg-[rgba(197,160,89,0.06)] transition">
              <LogOut className="w-4 h-4" /> Sign out
            </button>
          </div>
        </div>
      </aside>

      {/* Content */}
      <main ref={mainRef} className="flex-1 lg:ml-0 min-w-0 h-full overflow-y-auto">
        <div className="sticky top-0 z-30 glass-panel border-b border-gold-subtle px-4 py-3 flex items-center justify-between">
          <button onClick={() => setOpen(true)} className="text-emerald-muted lg:invisible" data-testid="sidebar-open-btn"><Menu className="w-5 h-5" /></button>
          <div className="flex items-center gap-2 font-heading text-xl text-white">
            <img src="/SOS_FA White Logo.png" alt="SOS Logo" className="h-8 w-auto min-w-[120px] min-h-[68px]" />
          </div>
          <div className="text-base text-white capitalize hidden sm:block" data-testid="header-user-role">{norm(user.name)}</div>
        </div>
        <div className="p-6 md:p-10 max-w-7xl mx-auto">
          <Outlet />
        </div>
      </main>
    </div>
  );
}